<template>
  <v-main style="height: 100%">
    <!-- Loading Overlay -->
    <v-overlay :value="loading">
      <div class="text-center">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
        <div class="mt-4 font-weight-medium white--text">
          {{ languageSelect === 0 ? '⚙️ กำลังโหลด กรุณารอสักครู่...' : '⚙️ Loading, please wait...' }}
        </div>
      </div>
    </v-overlay>

    <!-- Header Banner -->
    <v-card
      flat
      tile
      class="martech-header d-flex align-center justify-center"
      height="200px"
      width="100%"
    >
      <div class="header-overlay d-flex flex-column align-center justify-center" style="width:100%">
        <div class="header-badge px-6 py-2 mb-1">
          <span class="header-logo-text">MARTECH</span>
          <span class="header-logo-year ml-2">2026</span>
        </div>
        <div class="header-sub white--text text-caption mt-1">
          {{ languageSelect === 0 ? 'รายละเอียดการนัดหมาย' : 'Booking Details' }}
        </div>
      </div>
    </v-card>

    <!-- Main Content Card -->
    <v-card
      class="pa-3 pb-16 mt-n8"
      color="white"
      style="min-height: 100vh; border-radius: 32px 32px 0 0;"
      elevation="0"
    >
      <!-- Avatar -->
      <v-row justify="center" class="mt-n10 mb-3">
        <v-col cols="auto">
          <v-avatar size="90" color="white" class="elevation-6" style="border: 3px solid #fff;">
            <v-img
              v-if="profile && profile.pictureUrl"
              :src="profile.pictureUrl"
              :alt="profile.displayName"
            ></v-img>
            <v-icon v-else size="52" color="indigo darken-2">mdi-account-circle</v-icon>
          </v-avatar>
        </v-col>
      </v-row>

      <!-- Language Toggle -->
      <div style="display:flex; justify-content:center;" class="mb-4">
        <v-chip
          class="mr-1 font-weight-bold"
          :color="languageSelect === 0 ? 'indigo darken-2' : 'grey lighten-3'"
          :text-color="languageSelect === 0 ? 'white' : 'grey darken-1'"
          @click="languageSelect = 0"
        >
          <v-avatar left>
            <v-img src="https://firebasestorage.googleapis.com/v0/b/betask-linked/o/picture-web%2Fflag-TH.png?alt=media&token=e4bd7ffa-aed9-47e3-9240-4dcb9d8d284e"></v-img>
          </v-avatar>
          TH
        </v-chip>
        <v-chip
          class="ml-1 font-weight-bold"
          :color="languageSelect === 1 ? 'indigo darken-2' : 'grey lighten-3'"
          :text-color="languageSelect === 1 ? 'white' : 'grey darken-1'"
          @click="languageSelect = 1"
        >
          <v-avatar left>
            <v-img src="https://firebasestorage.googleapis.com/v0/b/betask-linked/o/picture-web%2Fflag-USA1.png?alt=media&token=a83dd820-f576-457a-8d08-1009cea9d70b"></v-img>
          </v-avatar>
          EN
        </v-chip>
      </div>

      <!-- Greeting Card -->
      <v-card
        v-if="profile"
        class="mx-2 mb-4 pa-4"
        color="indigo lighten-5"
        elevation="0"
        style="border-radius: 16px; border: 1.5px solid #c5cae9;"
      >
        <div class="d-flex align-center" style="gap: 12px;">
          <div>
            <div class="text-caption grey--text text--darken-1 mb-1">
              {{ languageSelect === 0 ? '👋 สวัสดี!' : '👋 Hello!' }}
            </div>
            <div class="font-weight-bold indigo--text text--darken-2" style="font-size:18px; line-height:1.3;">
              {{ profile.displayName || (languageSelect === 0 ? 'ผู้เข้าร่วมงาน' : 'Attendee') }}
            </div>
            <div
              v-if="profile.statusMessage"
              class="text-caption grey--text text--darken-1 mt-1"
              style="font-style: italic;"
            >
              "{{ profile.statusMessage }}"
            </div>
          </div>
        </div>
        <v-divider class="my-3"></v-divider>
        <div class="text-body-2 grey--text text--darken-2">
          {{ languageSelect === 0
            ? 'นี่คือรายละเอียดการนัดหมายของคุณ กรุณาตรวจสอบข้อมูลก่อนยืนยัน'
            : 'Here are your booking details. Please review before confirming.' }}
        </div>
      </v-card>

      <!-- No Booking State -->
      <v-card
        v-if="!loading && bookingList.length === 0"
        class="mx-2 mb-4 pa-6 text-center"
        elevation="0"
        style="border-radius: 16px; border: 1.5px dashed #c5cae9;"
      >
        <v-icon size="64" color="indigo lighten-3" class="mb-3">mdi-calendar-remove-outline</v-icon>
        <div class="font-weight-bold indigo--text text--darken-2 mb-1" style="font-size:16px;">
          {{ languageSelect === 0 ? 'ไม่พบรายการนัดหมาย' : 'No Bookings Found' }}
        </div>
        <div class="text-caption grey--text">
          {{ languageSelect === 0 ? 'คุณยังไม่มีนัดหมายในขณะนี้' : 'You have no active bookings at the moment.' }}
        </div>
      </v-card>

      <!-- Booking Cards -->
      <div v-for="(booking, index) in bookingList" :key="index">
        <v-card
          class="mx-2 mb-4"
          elevation="0"
          style="border-radius: 20px; border: 1.5px solid #e8eaf6; overflow: hidden;"
        >
          <!-- Card Header Strip -->
          <div class="booking-card-header d-flex align-center justify-space-between px-4 py-3">
            <div class="d-flex align-center">
              <v-icon color="white" size="18" class="mr-2">mdi-calendar-check</v-icon>
              <span class="white--text font-weight-bold text-body-2">
                {{ languageSelect === 0 ? 'การนัดหมาย' : 'Appointment' }}
                #{{ index + 1 }}
              </span>
            </div>
            <div class="d-flex align-center" style="gap:6px;">
              <v-chip x-small color="white" text-color="indigo darken-2" class="font-weight-bold">
                {{ booking.flowName || (languageSelect === 0 ? 'บริการ' : 'Service') }}
              </v-chip>
              <!-- statusBt badge -->
              <v-chip
                x-small
                :color="booking.statusBt === null ? 'amber lighten-3' : booking.statusBt === 'confirm' ? 'green lighten-3' : 'red lighten-3'"
                :text-color="booking.statusBt === null ? 'amber darken-3' : booking.statusBt === 'confirm' ? 'green darken-2' : 'red darken-2'"
                class="font-weight-bold"
              >
                {{ booking.statusBt === null ? (languageSelect === 0 ? 'รอยืนยัน' : 'Pending') : booking.statusBt === 'confirm' ? (languageSelect === 0 ? 'ยืนยันแล้ว' : 'Confirmed') : (languageSelect === 0 ? 'ยกเลิก' : 'Cancelled') }}
              </v-chip>
            </div>
          </div>

          <!-- Card Body -->
          <div class="pa-4">
            <!-- Date / Time -->
            <div class="info-row d-flex align-start mb-3">
              <v-icon color="indigo darken-2" size="20" class="mr-3 mt-1">mdi-clock-outline</v-icon>
              <div>
                <div class="text-caption grey--text text--darken-1">
                  {{ languageSelect === 0 ? 'วันที่ / เวลานัดหมาย' : 'Date / Time' }}
                </div>
                <div class="font-weight-bold indigo--text text--darken-2" style="font-size:15px;">
                  {{ booking.dueDateText || booking.dueDate || '-' }}
                </div>
              </div>
            </div>

            <v-divider class="mb-3"></v-divider>

            <!-- Shop & Branch -->
            <v-row no-gutters class="mb-2">
              <v-col cols="6">
                <div class="info-item pa-2">
                  <div class="text-caption grey--text text--darken-1 mb-1">
                    <v-icon size="14" color="grey darken-1" class="mr-1">mdi-store-outline</v-icon>
                    {{ languageSelect === 0 ? 'ชื่อร้าน' : 'Shop' }}
                  </div>
                  <div class="font-weight-medium" style="font-size:13px; color:#333;">
                    {{ shopName || '-' }}
                  </div>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="info-item pa-2">
                  <div class="text-caption grey--text text--darken-1 mb-1">
                    <v-icon size="14" color="grey darken-1" class="mr-1">mdi-map-marker-outline</v-icon>
                    {{ languageSelect === 0 ? 'สาขา' : 'Branch' }}
                  </div>
                  <div class="font-weight-medium" style="font-size:13px; color:#333;">
                    {{ booking.masBranchName || '-' }}
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Extra Fields from BookingData -->
            <v-row
              no-gutters
              v-if="booking.bookingData"
            >
              <v-col
                cols="6"
                v-for="(field, fi) in booking.bookingData"
                :key="fi"
              >
                <div class="info-item pa-2">
                  <div class="text-caption grey--text text--darken-1 mb-1">
                    <v-icon size="14" color="grey darken-1" class="mr-1">mdi-tag-outline</v-icon>
                    {{ languageSelect === 0 ? field.fieldName : (field.fieldNameEn || field.fieldName) }}
                  </div>
                  <div class="font-weight-medium" style="font-size:13px; color:#333;">
                    {{ field.fieldValue || '-' }}
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-3"></v-divider>

            <!-- Action Buttons: แสดงเฉพาะเมื่อ statusBt === null -->
            <v-row v-if="booking.statusBt === null" no-gutters class="mt-1" style="gap: 10px;">
              <v-col>
                <v-btn
                  block
                  dark
                  color="indigo darken-2"
                  style="border-radius: 12px;"
                  :loading="confirmingIndex === index"
                  @click="openConfirmDialog(booking, index)"
                >
                  <v-icon left size="18">mdi-check-circle-outline</v-icon>
                  {{ languageSelect === 0 ? 'ยืนยันนัดหมาย' : 'Confirm' }}
                </v-btn>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  icon
                  outlined
                  color="red darken-1"
                  style="border-radius: 12px; width:48px; height:48px;"
                  @click="openDeleteDialog(booking, index)"
                >
                  <v-icon size="20">mdi-trash-can-outline</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <!-- Already actioned: ซ่อนปุ่ม แสดงข้อความแทน -->
            <div v-else class="text-center py-2">
              <v-chip
                :color="booking.statusBt === 'confirm' ? 'green lighten-4' : 'red lighten-4'"
                :text-color="booking.statusBt === 'confirm' ? 'green darken-2' : 'red darken-2'"
                class="font-weight-bold"
              >
                <v-icon left small>{{ booking.statusBt === 'confirm' ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                {{ booking.statusBt === 'confirm'
                  ? (languageSelect === 0 ? 'ยืนยันนัดหมายแล้ว' : 'Booking Confirmed')
                  : (languageSelect === 0 ? 'ยกเลิกนัดหมายแล้ว' : 'Booking Cancelled') }}
              </v-chip>
            </div>
          </div>
        </v-card>
      </div>

      <!-- Footer -->
      <p class="text-center grey--text text--lighten-1 mt-4" style="font-size:11px;">
        Powered by <strong>BeTask</strong>
      </p>
    </v-card>

    <!-- FAB: เปิด list รายการทั้งหมด -->
    <v-btn
      fab
      fixed
      bottom
      right
      color="indigo darken-2"
      dark
      style="bottom: 24px; right: 20px; z-index:10;"
      @click="openBookingListAll"
      :loading="loadingListAll"
    >
      <v-icon>mdi-format-list-bulleted</v-icon>
    </v-btn>

    <!-- Confirm Dialog -->
    <v-dialog v-model="dialogConfirm" max-width="340" persistent>
      <v-card style="border-radius: 20px;" class="pa-2">
        <v-card-title class="justify-center pt-5 pb-2">
          <v-icon size="52" color="indigo darken-2" class="mr-2">mdi-calendar-check-outline</v-icon>
        </v-card-title>
        <v-card-title class="justify-center font-weight-bold pt-0" style="font-size:18px;">
          {{ languageSelect === 0 ? 'ยืนยันนัดหมาย?' : 'Confirm Booking?' }}
        </v-card-title>
        <v-card-text class="text-center grey--text text--darken-1 pb-2">
          {{ languageSelect === 0
            ? 'คุณต้องการยืนยันการนัดหมายนี้ใช่ไหม?'
            : 'Are you sure you want to confirm this booking?' }}
        </v-card-text>
        <v-card-actions class="pb-4 px-4 d-flex" style="gap:10px;">
          <v-btn
            outlined
            color="grey darken-1"
            style="border-radius: 12px; flex:1;"
            @click="dialogConfirm = false"
          >
            {{ languageSelect === 0 ? 'ยกเลิก' : 'Cancel' }}
          </v-btn>
          <v-btn
            dark
            color="indigo darken-2"
            style="border-radius: 12px; flex:1;"
            :loading="submitting"
            @click="confirmBooking"
          >
            <v-icon left size="16">mdi-check</v-icon>
            {{ languageSelect === 0 ? 'ยืนยัน' : 'Confirm' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <v-dialog v-model="dialogDelete" max-width="340" persistent>
      <v-card style="border-radius: 20px;" class="pa-2">
        <v-card-title class="justify-center pt-5 pb-2">
          <v-icon size="52" color="red darken-1">mdi-trash-can-outline</v-icon>
        </v-card-title>
        <v-card-title class="justify-center font-weight-bold pt-0" style="font-size:18px;">
          {{ languageSelect === 0 ? 'ลบนัดหมาย?' : 'Delete Booking?' }}
        </v-card-title>
        <v-card-text class="text-center grey--text text--darken-1 pb-2">
          {{ languageSelect === 0
            ? 'การลบนัดหมายจะไม่สามารถย้อนกลับได้ คุณแน่ใจหรือไม่?'
            : 'This action cannot be undone. Are you sure?' }}
        </v-card-text>
        <v-card-actions class="pb-4 px-4 d-flex" style="gap:10px;">
          <v-btn
            outlined
            color="grey darken-1"
            style="border-radius: 12px; flex:1;"
            @click="dialogDelete = false"
          >
            {{ languageSelect === 0 ? 'ยกเลิก' : 'Cancel' }}
          </v-btn>
          <v-btn
            dark
            color="red darken-1"
            style="border-radius: 12px; flex:1;"
            :loading="deleting"
            @click="deleteBooking"
          >
            <v-icon left size="16">mdi-delete</v-icon>
            {{ languageSelect === 0 ? 'ลบ' : 'Delete' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Dialog -->
    <v-dialog v-model="dialogSuccess" max-width="320" persistent>
      <v-card class="text-center pa-4" style="border-radius: 20px;">
        <v-icon size="64" color="success" class="mb-2 mt-4">mdi-check-circle</v-icon>
        <v-card-title class="justify-center font-weight-bold">
          {{ successTitle }}
        </v-card-title>
        <v-card-text class="grey--text">
          {{ successMessage }}
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="indigo darken-2" dark rounded style="border-radius: 12px; min-width:120px;" @click="dialogSuccess = false">
            {{ languageSelect === 0 ? 'ตกลง' : 'OK' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bottom Sheet: รายการนัดหมายทั้งหมด (statusBt = null) -->
    <v-bottom-sheet v-model="dialogBookingList" scrollable max-width="520">
      <v-card style="border-radius: 24px 24px 0 0; max-height: 80vh;">
        <!-- Header -->
        <div class="bottom-sheet-header d-flex align-center justify-space-between px-5 py-4">
          <div>
            <div class="white--text font-weight-bold" style="font-size:17px;">
              <v-icon color="white" size="20" class="mr-1">mdi-calendar-clock</v-icon>
              {{ languageSelect === 0 ? 'รายการรอยืนยัน' : 'Pending Bookings' }}
            </div>
            <div class="white--text text-caption" style="opacity:.75;">{{ bookingListAll.length }} {{ languageSelect === 0 ? 'รายการ' : 'item(s)' }}</div>
          </div>
          <v-btn icon dark @click="dialogBookingList = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- No data -->
        <div v-if="bookingListAll.length === 0" class="pa-8 text-center">
          <v-icon size="52" color="grey lighten-2" class="mb-3">mdi-calendar-remove-outline</v-icon>
          <div class="grey--text">{{ languageSelect === 0 ? 'ไม่มีรายการรอยืนยัน' : 'No pending bookings' }}</div>
        </div>

        <!-- List -->
        <v-list two-line class="pa-2" style="overflow-y:auto;">
          <v-list-item
            v-for="(item, i) in bookingListAll"
            :key="i"
            class="booking-list-item mb-2"
            @click="goToBooking(item)"
          >
            <v-list-item-avatar color="indigo lighten-5" size="44">
              <v-icon color="indigo darken-2" size="22">mdi-calendar-check</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold indigo--text text--darken-2" style="font-size:14px;">
                {{ item.flowName || '-' }}
              </v-list-item-title>
              <v-list-item-subtitle class="grey--text text--darken-1" style="font-size:12px;">
                <v-icon size="12" class="mr-1">mdi-clock-outline</v-icon>{{ item.dueDateText || item.dueDate || '-' }}
                &nbsp;·&nbsp;
                <v-icon size="12" class="mr-1">mdi-map-marker-outline</v-icon>{{ item.masBranchName || '-' }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-chip x-small color="amber lighten-3" text-color="amber darken-3" class="font-weight-bold">
                {{ languageSelect === 0 ? 'รอยืนยัน' : 'Pending' }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
    </v-bottom-sheet>

    <v-dialog v-model="dialogError" max-width="320" persistent>
      <v-card class="text-center pa-4" style="border-radius: 20px;">
        <v-icon size="64" color="error" class="mb-2 mt-4">mdi-close-circle</v-icon>
        <v-card-title class="justify-center font-weight-bold">
          {{ errorTitle }}
        </v-card-title>
        <v-card-text class="grey--text">
          {{ errorMessage }}
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-main>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

export default {
  name: 'Martech2026ConfirmBooking',
  data () {
    return {
      dialogError: false,
      errorTitle: '',
      errorMessage: '',
      loading: false,
      submitting: false,
      deleting: false,
      languageSelect: 0,
      shopId: '',
      shopName: '',
      shopItem: [],
      profile: null,
      bookingList: [],
      bookingListAllNull: [],
      selectedBooking: null,
      selectedIndex: null,
      confirmingIndex: null,
      dialogConfirm: false,
      dialogDelete: false,
      dialogSuccess: false,
      successTitle: '',
      successMessage: '',
      branchId: '',
      dialogBookingList: false,
      bookingListAll: [],
      loadingListAll: false,
      bookingNo: ''
    }
  },
  async mounted () {
    this.loading = true
    try {
      let checkShopId = ''
      if (this.$route.query.shopId) {
        checkShopId = this.$route.query.shopId
      } else {
        const queryString = decodeURIComponent(window.location.search).replace('?liff.state=', '')
        const params = new URLSearchParams(queryString)
        checkShopId = params.get('shopId')
      }
      this.shopId = checkShopId
      this.bookingNo = this.$route.query.bookNo
      let dataLineConfig = await this.getDataLineConfig(checkShopId)
      await this.checkLiffLogin(dataLineConfig)
      await this.getShopInfo()
    } catch (e) {
      console.error('mounted error', e)
    } finally {
      this.loading = false
    }
  },
  methods: {
    async getShopInfo () {
      if (!this.shopId) return
      await axios.get(this.DNS_IP + '/sys_shop/get?shopId=' + this.shopId).then(response => {
        let rs = response.data
        if (rs && rs.length > 0) {
          this.shopItem = rs
          this.shopName = rs[0].shopName || ''
        }
      }).catch(err => console.error('getShopInfo err', err))
    },
    async checkLiffLogin (dataLineConfig) {
      await this.$liff
        .init({ liffId: dataLineConfig.liffMainID })
        .then(async () => {
          if (process.env.NODE_ENV === 'development') {
            this.getProfile_dev()
          } else {
            if (!this.$liff.isLoggedIn()) {
              this.$liff.login({ redirectUri: window.location.href })
            } else {
              await this.getProfile()
            }
          }
        })
        .catch(err => {
          console.error(err.code, err.message)
        })
    },
    async getProfile () {
      let _this = this
      await this.$liff.getProfile().then(function (profile) {
        _this.profile = profile
        console.log('prod', _this.profile)
      }).catch(function (error) {
        console.error('Error getting profile: ' + error)
      })
      await _this.getBookingList(this.bookingNo, false)
    },
    getProfile_dev () {
      this.profile = this.$profile_dev
      this.getBookingList(this.bookingNo, false)
    },
    async getBookingList (bookNo, checkList) {
      if (!bookNo) return
      this.loading = true
      this.bookingList = []
      try {
        const response = await axios.get(
          this.DNS_IP + '/booking_view/get?bookNo=' + bookNo
        )
        let rs = response.data
        if (rs && rs.status !== false && Array.isArray(rs) && rs.length > 0) {
          // โหลด extra fields จาก BookingData
          for (let i = 0; i < rs.length; i++) {
            rs[i].bookingData = await this.getBookingData(rs[i].bookNo)
          }
          if (!checkList) {
            await this.checkMember(rs[0].masBranchID)
          }
          this.bookingList = rs
        } else {
          this.bookingList = []
        }
      } catch (err) {
        console.error('getBookingList err', err)
        this.bookingList = []
      } finally {
        this.loading = false
      }
    },
    async checkMember (masBranchId) {
      try {
        const response = await axios.get(
          this.DNS_IP + '/member/get?liffUserId=' + this.profile.userId
        )
        let rs = response.data
        if (rs && rs.status !== false && Array.isArray(rs) && rs.length > 0) {
          if (rs[0].boothEmpStatus === 'True' && rs[0].boothEmpBranch === masBranchId) {
            this.branchId = rs[0].boothEmpBranch
          } else {
            this.dialogError = true
            this.errorTitle = 'ผิดพลาด'
            this.errorMessage = 'คุณไม่ใช่พนักงานบูธ กรุณาติดต่อผู้ดูแลระบบ'
          }
        } else {
          this.dialogError = true
          this.errorTitle = 'ผิดพลาด'
          this.errorMessage = 'ไม่สามารถดึงข้อมูลได้ กรุณาติดต่อผู้ดูแลระบบ'
        }
      } catch (err) {
        console.error('checkMember err', err)
        this.$swal('ผิดพลาด', 'ไม่สามารถดึงข้อมูลได้ กรุณาลองใหม่', 'error')
      }
    },
    // ดึง extra fields จาก BookingData/getView per bookNo
    async getBookingData (bookNo) {
      try {
        const response = await axios.get(
          this.DNS_IP + '/BookingData/getView?bookNo=' + bookNo
        )
        let rs = response.data
        return Array.isArray(rs) ? rs : []
      } catch (err) {
        console.error('getBookingData err', err)
        return []
      }
    },
    openConfirmDialog (booking, index) {
      this.selectedBooking = booking
      this.selectedIndex = index
      this.dialogConfirm = true
    },
    openDeleteDialog (booking, index) {
      this.selectedBooking = booking
      this.selectedIndex = index
      this.dialogDelete = true
    },
    // ยืนยันนัดหมาย -> booking_transaction/add ด้วย status = confirm
    async confirmBooking () {
      if (!this.selectedBooking) return
      this.submitting = true
      try {
        const dtt = {
          bookNo: this.selectedBooking.bookNo,
          contactDate: moment().format('YYYY-MM-DD HH:mm'),
          status: 'confirm',
          statusUse: 'use',
          shopId: this.shopId,
          CREATE_USER: this.profile.userId,
          LAST_USER: this.profile.userId
        }
        await axios.post(this.DNS_IP + '/booking_transaction/add', dtt)
        await this.pushmessage('confirm', this.selectedBooking)
        this.dialogConfirm = false
        this.successTitle = this.languageSelect === 0 ? '✅ ยืนยันสำเร็จ!' : '✅ Booking Confirmed!'
        this.successMessage = this.languageSelect === 0
          ? 'ยืนยันนัดหมายเรียบร้อยแล้ว'
          : 'Your booking has been confirmed successfully.'
        this.dialogSuccess = true
        await this.getBookingList(this.bookingNo, true)
      } catch (err) {
        console.error('confirmBooking err', err)
        this.$swal('ผิดพลาด', 'ไม่สามารถยืนยันนัดหมายได้ กรุณาลองใหม่', 'error')
      } finally {
        this.submitting = false
        this.selectedBooking = null
        this.selectedIndex = null
      }
    },
    // ยกเลิกนัดหมาย -> booking_transaction/add ด้วย status = cancel
    async deleteBooking () {
      if (!this.selectedBooking) return
      this.deleting = true
      try {
        const dtt = {
          bookNo: this.selectedBooking.bookNo,
          contactDate: moment().format('YYYY-MM-DD HH:mm'),
          status: 'cancel',
          statusUse: 'use',
          shopId: this.shopId,
          CREATE_USER: this.profile.userId,
          LAST_USER: this.profile.userId
        }
        await axios.post(this.DNS_IP + '/booking_transaction/add', dtt)
        await this.pushmessage('cancel', this.selectedBooking)
        this.dialogDelete = false
        this.successTitle = this.languageSelect === 0 ? '🗑️ ยกเลิกนัดหมายแล้ว' : '🗑️ Booking Cancelled'
        this.successMessage = this.languageSelect === 0
          ? 'ยกเลิกนัดหมายเรียบร้อยแล้ว'
          : 'Your booking has been successfully cancelled.'
        this.dialogSuccess = true
        await this.getBookingList(this.bookingNo, true)
      } catch (err) {
        console.error('deleteBooking err', err)
        this.$swal('ผิดพลาด', 'ไม่สามารถยกเลิกนัดหมายได้ กรุณาลองใหม่', 'error')
      } finally {
        this.deleting = false
        this.selectedBooking = null
        this.selectedIndex = null
      }
    },
    // เปิด bottom sheet และดึงรายการทั้งหมด statusBt=null
    async openBookingListAll () {
      this.dialogBookingList = true
      await this.getBookingListAllNull()
    },
    async getBookingListAllNull () {
      if (!this.shopId) return
      this.loadingListAll = true
      this.bookingListAll = []
      try {
        const response = await axios.get(
          this.DNS_IP + '/booking_view/get?shopId=' + this.shopId + '&statusBt=null&masBranchID=' + this.branchId
        )
        let rs = response.data
        if (rs && rs.status !== false && Array.isArray(rs)) {
          this.bookingListAll = rs
        }
      } catch (err) {
        console.error('getBookingListAllNull err', err)
      } finally {
        this.loadingListAll = false
      }
    },
    // คลิกรายการ ไป หน้า confirmBooking พร้อม bookNo นั้นๆ
    goToBooking (item) {
      this.dialogBookingList = false
      this.getBookingList(item.bookNo, true)
    },
    async pushmessage (type, booking) {
      try {
        if (type === 'confirm') {
          await axios.post(this.DNS_IP + '/Booking/pushMsgConfirm/' + booking.bookNo)
        } else if (type === 'cancel') {
          const body = {
            shopId: this.shopId,
            bookNo: booking.bookNo,
            lineUserId: this.profile.userId,
            dueDate: booking.dueDateText || booking.dueDate || '',
            flowName: booking.flowName || '',
            tell: this.shopItem.length > 0 ? (this.shopItem[0].contactTel || '') : ''
          }
          await axios.post(this.DNS_IP + '/Booking/pushMsgCancelBook', body)
        }
        console.log('pushmessage', type, booking.bookNo)
      } catch (err) {
        console.error('pushmessage err', err)
      }
    }
  }
}
</script>

<style scoped>
/* Header Banner */
.martech-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 40%, #3949ab 100%);
  position: relative;
  overflow: hidden;
}
.martech-header::before {
  content: '';
  position: absolute;
  top: -60px;
  right: -60px;
  width: 220px;
  height: 220px;
  background: rgba(255, 255, 255, 0.07);
  border-radius: 50%;
}
.martech-header::after {
  content: '';
  position: absolute;
  bottom: -40px;
  left: -40px;
  width: 160px;
  height: 160px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
}
.header-overlay {
  position: relative;
  z-index: 1;
}
.header-badge {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50px;
  display: inline-flex;
  align-items: baseline;
}
.header-logo-text {
  font-size: 28px;
  font-weight: 800;
  color: white;
  letter-spacing: 5px;
}
.header-logo-year {
  font-size: 20px;
  font-weight: 700;
  color: #90caf9;
}
.header-sub {
  letter-spacing: 1.5px;
  opacity: 0.8;
}

/* Booking Card Header Strip */
.booking-card-header {
  background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
}

/* Info Items */
.info-item {
  background: #f5f5f5;
  border-radius: 10px;
  min-height: 52px;
}

/* Bottom Sheet Header */
.bottom-sheet-header {
  background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
  border-radius: 24px 24px 0 0;
}

/* Booking List Item */
.booking-list-item {
  background: #f5f7ff;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s;
}
.booking-list-item:hover {
  background: #e8eaf6;
}
</style>



// WEBPACK FOOTER //
// src/components/Martech2026/confirmBooking.vue